import { Base } from '@studiometa/js-toolkit';

const ENDPOINT = 'https://api.vatcomply.com/vat_rates';
const CACHE_KEY = 'price-b2b:vat_rates';
const CACHE_TTL = 24 * 60 * 60 * 1000;

// Pays hors UE non couverts par VatComply.
const NON_EU_RATES = {
  GB: 20,
};

let ratesPromise = null;

// Caractères acceptés comme séparateurs de milliers / espaces dans une chaîne
// monétaire (point, virgule, apostrophe, espace, espace insécable, espace fin).
const SEPARATOR_CHARS = ".,'\\s\\u00A0\\u202F";

/**
 * Convertit le prix TTC affiché (rendu côté Liquid) en HT, en préservant
 * exactement le format monétaire d'origine (symbole, séparateurs, suffixe).
 */
export default class PriceB2b extends Base {
  static config = {
    name: 'PriceB2b',
    options: {
      country: String,
    },
  };

  /**
   * Récupère le taux du pays et remplace la valeur numérique du textContent
   * par sa version HT.
   * @returns {Promise<void>}
   */
  async mounted() {
    const { country } = this.$options;

    if (!country) {
      return;
    }

    const originalText = this.$el.textContent;

    try {
      const rate = await getRate(country.toUpperCase());
      const factor = 1 / (1 + rate / 100);
      this.$el.textContent = scaleFormattedMoney(originalText, factor);
      this.$el.dataset.b2bStatus = 'ok';
    } catch {
      this.$el.dataset.b2bStatus = 'fallback';
    }
  }
}

/**
 * Lit la table de taux mise en cache dans sessionStorage si elle est encore valide.
 * @returns {{[key: string]: number} | null}
 */
function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { rates, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) return null;
    return rates;
  } catch {
    return null;
  }
}

/**
 * Persiste la table de taux dans sessionStorage.
 * @param {{[key: string]: number}} rates
 * @returns {void}
 */
function writeCache(rates) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ rates, ts: Date.now() }));
  } catch {
    // sessionStorage indisponible ou plein : on ignore, le cache mémoire suffit.
  }
}

/**
 * Récupère la table des taux de TVA depuis VatComply (1 seul appel partagé).
 * @returns {Promise<{[key: string]: number}>}
 */
async function loadRates() {
  if (ratesPromise) return ratesPromise;

  const cached = readCache();
  if (cached) {
    ratesPromise = Promise.resolve(cached);
    return ratesPromise;
  }

  ratesPromise = fetch(ENDPOINT)
    .then((response) => {
      if (!response.ok) throw new Error(`VatComply HTTP ${response.status}`);
      return response.json();
    })
    .then((data) => {
      const rates = {};
      if (Array.isArray(data)) {
        for (const entry of data) {
          if (entry?.country_code && typeof entry.standard_rate === 'number') {
            rates[entry.country_code] = entry.standard_rate;
          }
        }
      }
      writeCache(rates);
      return rates;
    })
    .catch((e) => {
      ratesPromise = null;
      throw e;
    });

  return ratesPromise;
}

/**
 * Retourne le taux de TVA standard pour un pays.
 * @param {string} country
 * @returns {Promise<number>}
 */
async function getRate(country) {
  try {
    const rates = await loadRates();
    if (typeof rates[country] === 'number') return rates[country];
  } catch {
    // VatComply indisponible : on tente le fallback hors-UE ci-dessous.
  }

  if (typeof NON_EU_RATES[country] === 'number') return NON_EU_RATES[country];

  throw new Error(`No VAT rate available for ${country}`);
}

/**
 * Trouve la valeur numérique dans une chaîne monétaire formatée, la multiplie
 * par un facteur et la reformate à l'identique (mêmes séparateurs, mêmes
 * décimales). Tous les caractères non numériques (symbole, suffixe code-devise,
 * mention TTC, etc.) sont préservés tels quels.
 *
 * Exemples :
 * "£8.00 GBP"  * 0.833 → "£6.67 GBP"
 * "68,80 €"    * 0.833 → "57,33 €"
 * "1 234,56 €" * 0.833 → "1 028,39 €"
 * "$1,234.56"  * 0.833 → "$1,028.39"
 * @param {string} formatted
 * @param {number} factor
 * @returns {string}
 */
function scaleFormattedMoney(formatted, factor) {
  // Séquence numérique : doit commencer ET se terminer par un chiffre, pour ne
  // jamais avaler l'espace qui sépare le nombre du symbole monétaire.
  const numberRegex = new RegExp(`\\d[\\d${SEPARATOR_CHARS}]*\\d|\\d`);
  const match = formatted.match(numberRegex);
  if (!match) return formatted;

  const numStr = match[0];

  // Séparateur décimal : un '.' ou ',' suivi de 1-2 chiffres en fin de nombre.
  const decimalMatch = numStr.match(/([.,])(\d{1,2})$/);
  const decimalSep = decimalMatch ? decimalMatch[1] : '';
  const decimals = decimalMatch ? decimalMatch[2].length : 0;
  const integerPart = decimalMatch ? numStr.slice(0, -(decimals + 1)) : numStr;

  // Séparateur de milliers : caractère récurrent entre chiffres dans la partie entière.
  const thousandsRegex = new RegExp(`\\d([${SEPARATOR_CHARS}])\\d`);
  const thousandsMatch = integerPart.match(thousandsRegex);
  const thousandsSep = thousandsMatch ? thousandsMatch[1] : '';

  const cleanedInteger = thousandsSep ? integerPart.split(thousandsSep).join('') : integerPart;

  const decimalDigits = decimalMatch ? decimalMatch[2] : '0';
  const value = Number.parseFloat(`${cleanedInteger}.${decimalDigits}`);
  if (Number.isNaN(value)) return formatted;

  const scaled = value * factor;
  const fixed = scaled.toFixed(decimals);
  const [intRaw, decRaw] = fixed.split('.');
  const intOut = thousandsSep ? intRaw.replaceAll(/\B(?=(\d{3})+(?!\d))/g, thousandsSep) : intRaw;
  const scaledStr = decRaw ? `${intOut}${decimalSep}${decRaw}` : intOut;

  return formatted.replace(numStr, scaledStr);
}
